import React, { useState, useEffect, useCallback } from 'react';
import { Box, Container, CircularProgress } from '@mui/material';
import Sidebar, {
  AdminContentType,
} from '../components/admin-dashboard/Sidebar';
import ProjectReviewList from '../components/admin-dashboard/ProjectReviewList';
import ProjectReviewDialog from '../components/admin-dashboard/ProjectReviewDialog';
import UserManagement from '../components/admin-dashboard/UserManagement';
import StudentsList from '../components/admin-dashboard/students/StudentsList';
import FormManagement from '../components/admin-dashboard/forms/FormManagement';
import StudentDetails from '../components/admin-dashboard/students/StudentDetails';
import AccountSettings from '../components/admin-dashboard/AccountSettingsContent';
import RegionDetails from '../components/admin-dashboard/RegionDetails';
import FairManagement from '../components/admin-dashboard/FairManagement';
// import { judgingService } from '../services/judging.service';
import { useAuth } from '../contexts/AuthContext';
import { projectsService } from '../services/projects.service';
import { projectFormsService } from '../services/project-forms.service';
import { Project } from '../types/project.types';
import { FormSubmission, ReviewStatus } from '../types/forms.types';
import { Student } from '../types/student.types';
import ProjectAssignment from '../components/admin-dashboard/ProjectAssignment'; // update the path if needed
import { getRegionStatus } from '../services/regions.services';
import ProjectScoresList from '../components/admin-dashboard/content/ProjectScoresList';
import RubricManagment from '../components/admin-dashboard/rubrics/RubricManagment';

interface ProjectWithForms extends Project {
  forms: FormSubmission[];
  totalForms: number;
  reviewedForms: number;
}

const AdminDashboard: React.FC = () => {
  const { authStatus } = useAuth();
  const [activeContent, setActiveContent] =
    useState<AdminContentType>('projects');
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [error, setError] = useState<string | null>(null);
  const [regionStatus, setRegionStatus] = useState<string | null>(null);
  // Existing score state
  // const [existingScore, setExistingScore] = useState<
  //   ProjectScore | undefined
  // >();
  // const [isScoringDialogOpen, setScoringDialogOpen] = useState(false);

  // Projects state
  const [projects, setProjects] = useState<ProjectWithForms[]>([]);
  const [selectedProjectID, setSelectedProjectID] = useState<string | null>(
    null
  );
  const [isProjectReviewOpen, setIsProjectReviewOpen] = useState(false);
  const [pendingProjectsCount, setPendingProjectsCount] = useState(0);

  // Students state
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [isStudentDetailsOpen, setIsStudentDetailsOpen] = useState(false);

  // alongside your existing review-state:

  const fetchRegionStatus = useCallback(async () => {
    try {
      const regionStatus = await getRegionStatus(
        authStatus.metadata?.regionId ?? ''
      );
      return regionStatus ?? null;
    } catch (err) {
      console.error('Error fetching region status:', err);
      return null;
    }
  }, [authStatus.metadata?.regionId]);

  const fetchData = useCallback(async () => {
    if (!authStatus.user?.uid) return;

    const status = await fetchRegionStatus();
    setRegionStatus(status);

    const unsubscribe = projectsService.subscribeToProjectsInRegionByFairs(
      authStatus.metadata?.regionId ?? '',
      (updatedProjects) => {
        Promise.all(
          updatedProjects.map(async (project) => {
            // Get forms for each project
            const forms = await new Promise<FormSubmission[]>((resolve) => {
              const unsubForms = projectFormsService.subscribeToProjectForms(
                project.id,
                (projectForms) => {
                  unsubForms(); // Unsubscribe immediately as we just want one snapshot
                  resolve(projectForms);
                }
              );
            });

            const reviewedForms = forms.filter(
              (form) => form.status === 'approved' || form.status === 'rejected'
            ).length;

            return {
              ...project,
              forms,
              totalForms: forms.length,
              reviewedForms,
            };
          })
        ).then((projectsWithForms) => {
          setProjects(projectsWithForms);
          setPendingProjectsCount(
            projectsWithForms.filter((p) => p.status === 'submitted').length
          );
        });
      }
    );
    return () => unsubscribe();
  }, [authStatus.user?.uid, authStatus.metadata?.regionId, fetchRegionStatus]);

  useEffect(() => {
    setLoading(true);
    fetchData();
    setLoading(false);
  }, [fetchData]);

  const handleViewProject = (projectID: string) => {
    setActiveContent('projects');
    setSelectedProjectID(projectID);
    setIsProjectReviewOpen(true);
  };

  const handleUpdateProjectStatus = async (
    projectId: string,
    status: ReviewStatus,
    comments?: string
  ) => {
    try {
      if (!authStatus.user?.uid) return;

      await projectsService.updateProjectStatus(
        projectId,
        status,
        authStatus.user.uid,
        comments
      );
      fetchData(); // Refresh data after status update
      setIsProjectReviewOpen(false);
      setSelectedProjectID(null);
    } catch (err) {
      console.error('Error updating project status:', err);
      setError('Failed to update project status');
    }
  };

  const handleFormReview = async (
    formId: string,
    status: ReviewStatus,
    comments: string
  ) => {
    try {
      if (!authStatus.user?.uid || !authStatus.metadata) return;

      const reviewerName = `${authStatus.metadata.firstName} ${authStatus.metadata.lastName}`;

      await projectFormsService.submitReview(
        formId,
        {
          reviewerId: authStatus.user.uid,
          reviewerName,
          reviewerEmail: authStatus.user.email,
          comments,
          role: 'primary',
          status,
        },
        status
      );
      fetchData(); // Refresh data after review submission
    } catch (err) {
      console.error('Error submitting form review:', err);
      throw err;
    }
  };

  const handleAssignReviewer = async (
    formId: string,
    reviewerId: string,
    reviewerName: string,
    role: 'primary' | 'secondary' | 'final'
  ) => {
    try {
      await projectFormsService.assignReviewer(
        formId,
        reviewerId,
        reviewerName,
        role
      );
    } catch (err) {
      console.error('Error assigning reviewer:', err);
      setError('Failed to assign reviewer');
    }
  };

  const handleViewStudent = (student: Student) => {
    setSelectedStudent(student);
    setIsStudentDetailsOpen(true);
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="400px"
        >
          <CircularProgress />
        </Box>
      );
    }
    if (regionStatus === 'pending') {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="400px"
        >
          <p style={{ textAlign: 'center' }}>
            Your Region is Pending Approval, Please Wait
            <br />
            <br />
            If you think this is a mistake or have waited longer than a week,
            <br />
            Please contact support at smnscifair@gmail.com
          </p>
        </Box>
      );
    } else if (regionStatus === 'suspended') {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="400px"
        >
          <p style={{ textAlign: 'center' }}>
            Your Region has been Suspended
            <br />
            <br />
            Please contact support at smnscifair@gmail.com
          </p>
        </Box>
      );
    }

    switch (activeContent) {
      case 'projects':
        return (
          <ProjectReviewList
            projects={projects}
            currentUserId={authStatus.user?.uid || ''}
            onViewProject={handleViewProject}
          />
        );
      case 'projectScores':
        return <ProjectScoresList projects={projects} />;
      case 'students':
        return <StudentsList onViewStudent={handleViewStudent} />;
      case 'assignment':
        return <ProjectAssignment />;
      case 'forms':
        return <FormManagement />;
      case 'rubrics':
        return <RubricManagment />;
      case 'settings':
        return <AccountSettings />;
      case 'region':
        return <RegionDetails />;
      case 'fair':
        return <FairManagement />;
      case 'users':
        return <UserManagement />;
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        bgcolor: 'background.default',
      }}
    >
      <Sidebar
        activeContent={activeContent}
        onContentChange={setActiveContent}
        pendingProjectsCount={pendingProjectsCount}
      />

      <Box component="main" sx={{ flexGrow: 1, p: 3, width: '100%' }}>
        <Container maxWidth="xl">
          <Box sx={{ py: 2 }}>{renderContent()}</Box>
        </Container>
      </Box>

      {/* Project Review Dialog */}
      {activeContent === 'projects' && selectedProjectID && (
        <ProjectReviewDialog
          open={isProjectReviewOpen}
          onClose={() => {
            setIsProjectReviewOpen(false);
            setSelectedProjectID(null);
          }}
          project={
            (projects.find(
              (p) => p.id === selectedProjectID
            ) as ProjectWithForms)!
          }
          onUpdateProjectStatus={handleUpdateProjectStatus}
          onSubmitFormReview={handleFormReview}
          onAssignReviewer={handleAssignReviewer}
        />
      )}

      {/* Student Details Dialog */}
      <StudentDetails
        open={isStudentDetailsOpen}
        onClose={() => {
          setIsStudentDetailsOpen(false);
          setSelectedStudent(null);
        }}
        student={selectedStudent}
      />
    </Box>
  );
};

export default AdminDashboard;
